import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { format } from 'date-fns'

import ProfessionalsService from 'services/professionals'

import {
  formatEvents,
  formatAvailabilityResponse,
  getDaysWithEvents,
  getMinAndMaxDatesFromDateList
} from './utils'

import {
  Availabilities,
  GetProfessionalAvailabilitiesArgs,
  UseGetProfessionalAvailabilitiesReturn
} from './types'

async function getProfessionalAvailabilities({
  restrictions,
  professionalUsername,
  patientUsername,
  productCode
}: GetProfessionalAvailabilitiesArgs): Promise<Availabilities[]> {

  const formattedStartAndEndDate = getMinAndMaxDatesFromDateList(restrictions)
  const formattedRestrictionDate = restrictions.map((item) => format(new Date(item), 'yyyy-MM-dd'))
  const response = await ProfessionalsService.listProfessionalAvailabilities({
    professionalUsername,
    patientUsername,
    startDate: formattedStartAndEndDate.startDate,
    endDate: formattedStartAndEndDate.endDate,
    productCode
  })

  const formattedResponse = formatAvailabilityResponse(response)
  const daysWithEvents = getDaysWithEvents(formattedResponse.availabilities, formattedRestrictionDate)

  return formatEvents(daysWithEvents)
}

export const useGetProfessionalAvailabilities = ({
  professionalUsername,
  patientUsername,
  restrictions,
  productCode
}: GetProfessionalAvailabilitiesArgs): UseQueryResult<UseGetProfessionalAvailabilitiesReturn[], unknown> => {
  const areAllParamsReady = Boolean(
    professionalUsername &&
    patientUsername &&
    restrictions &&
    restrictions.length > 0 &&
    productCode
  )
  return useQuery({
    queryKey: [
      'professional-availabilities',
      professionalUsername,
      patientUsername,
      restrictions,
      productCode
    ],
    queryFn: () => getProfessionalAvailabilities({
      restrictions,
      patientUsername,
      professionalUsername,
      productCode,
    }),
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: areAllParamsReady,
  })
}
